import { useState } from "react";
import { RotateCw, Star, Trash2 } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { Button } from "@/components/Button";
import { EmptyState, ErrorState, Spinner } from "@/components/states";
import { type FavKind, useFavorites, useRemoveFavorite } from "@/hooks/useFavorites";
import { filtersSummary } from "@/lib/filters";
import { useHuntStore } from "@/stores/hunt";
import type {
  Favorite,
  FavIdeaPayload,
  FavRepoPayload,
  FavSearchPayload,
  SearchRequest,
} from "@/types";

const TABS: { key: FavKind; label: string }[] = [
  { key: "search", label: "Поиски" },
  { key: "repo", label: "Репозитории" },
  { key: "idea", label: "Идеи" },
];

export function Favorites() {
  const [tab, setTab] = useState<FavKind>("search");
  const { data, isLoading, isError } = useFavorites(tab);
  const remove = useRemoveFavorite();
  const navigate = useNavigate();
  const setReplay = useHuntStore((s) => s.setReplay);

  function replay(req: SearchRequest) {
    setReplay(req);
    navigate("/hunt");
  }

  return (
    <main className="mx-auto w-full max-w-4xl px-6 py-6">
      <h1 className="mb-4 flex items-center gap-2 text-lg font-semibold text-ink-1">
        <Star size={18} className="text-accent" /> Избранное
      </h1>
      <div className="mb-4 flex gap-2">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`rounded-lg border px-3 py-1.5 text-sm ${
              tab === t.key ? "border-accent text-accent" : "border-line text-ink-2 hover:text-ink-1"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {isLoading && <Spinner />}
      {isError && <ErrorState>Не удалось загрузить избранное.</ErrorState>}
      {data && data.length === 0 && <EmptyState>Пока пусто.</EmptyState>}
      <div className="space-y-2">
        {data?.map((f) => (
          <Row
            key={f.id}
            fav={f}
            onRemove={() => remove.mutate(f.id)}
            onReplay={replay}
            removing={remove.isPending}
          />
        ))}
      </div>
    </main>
  );
}

function Row({
  fav,
  onRemove,
  onReplay,
  removing,
}: {
  fav: Favorite;
  onRemove: () => void;
  onReplay: (r: SearchRequest) => void;
  removing?: boolean;
}) {
  return (
    <div className="flex items-start justify-between gap-3 rounded-lg border border-line bg-bg-1 px-4 py-3">
      <div className="min-w-0 flex-1">
        {fav.kind === "search" && <SearchFav p={fav.payload as FavSearchPayload} onReplay={onReplay} />}
        {fav.kind === "repo" && <RepoFav p={fav.payload as FavRepoPayload} />}
        {fav.kind === "idea" && <IdeaFav p={fav.payload as FavIdeaPayload} />}
      </div>
      <button
        onClick={onRemove}
        disabled={removing}
        title="Убрать"
        className="mt-0.5 text-ink-3 hover:text-err disabled:opacity-50"
      >
        <Trash2 size={16} />
      </button>
    </div>
  );
}

function SearchFav({ p, onReplay }: { p: FavSearchPayload; onReplay: (r: SearchRequest) => void }) {
  const summary = filtersSummary(p.filters);
  return (
    <div className="flex items-center justify-between gap-3">
      <div className="min-w-0">
        <div className="truncate font-medium text-ink-1">{p.preset ? `Пресет: ${p.preset}` : p.query || "(пусто)"}</div>
        {summary && <div className="truncate text-xs text-ink-3">{summary}</div>}
      </div>
      <Button
        variant="ghost"
        onClick={() => onReplay(p.preset ? { preset: p.preset, filters: p.filters } : { query: p.query ?? "", filters: p.filters })}
      >
        <RotateCw size={14} /> Повторить
      </Button>
    </div>
  );
}

function RepoFav({ p }: { p: FavRepoPayload }) {
  return (
    <div className="min-w-0">
      <a href={p.html_url ?? "#"} target="_blank" rel="noreferrer" className="font-medium text-ink-1 hover:text-accent">
        {p.full_name}
      </a>
      <span className="ml-2 text-xs text-ink-3">★ {p.stars ?? "—"} · {p.language ?? "—"}</span>
      {p.description && <div className="truncate text-xs text-ink-2">{p.description}</div>}
    </div>
  );
}

function IdeaFav({ p }: { p: FavIdeaPayload }) {
  return (
    <div className="min-w-0">
      <div className="font-medium text-ink-1">{p.idea.title}</div>
      {p.idea.why_specific && <div className="text-sm text-ink-2">{p.idea.why_specific}</div>}
      <a href={p.html_url} target="_blank" rel="noreferrer" className="text-xs text-accent hover:underline">
        {p.full_name}
      </a>
    </div>
  );
}
